/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }

function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

import { combineReducers } from 'redux';
import SeamlessImmutable from 'seamless-immutable';
import { unique } from 'underscore';
import File from 'DesignData/models/finder/File';
import { FINDER_FILE_CREATE, FINDER_FILE_CREATE_FROM_EDITOR, FINDER_FILE_DELETE, FINDER_FILE_DUPLICATE, FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_DUPLICATE_READ_ONLY, FINDER_FILE_RENAME, FINDER_FILE_RESTORE_DELETED, FINDER_FILE_UPLOAD_FINISHED, FINDER_FILE_UPLOAD_UPDATE_PROGRESS, FINDER_FILES_BULK_DELETE, FINDER_FILES_RECENTLY_DELETED_FETCH, FINDER_FILES_RECENTLY_EDITED_FETCH, FINDER_ASSETS_SEARCH_FETCH, FINDER_FOLDER_CONTENTS_BULK_DELETE, FINDER_FOLDER_CONTENTS_FETCH, FINDER_FOLDER_DELETE, FINDER_FOLDER_PARENTS_FETCH, FINDER_OPEN_FOLDERS_FETCH, FINDER_ROOT_FOLDER_CONTENTS_FETCH, FINDER_FILEMAPPER_FETCH_DEFAULT // Actions to listen to from editors
, LAYOUT_RENAME, TEMPLATE_BUFFER_PUBLISH, RAW_ASSET_CREATE } from 'DesignData/actions/ActionTypes';
import { FOLDER } from 'DesignData/constants/FolderAssetTypes';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { getFileDataFromResult } from 'DesignData/helpers/finderHelpers';

function mergeFiles(state, files) {
  const newFiles = files.reduce((filesById, file) => {
    const current = state[file.id] || null;
    filesById[file.id] = new File(Object.assign({}, current, {}, file));
    return filesById;
  }, {});
  return SeamlessImmutable.merge(state, newFiles, {
    deep: true
  });
}

function byId(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_FILEMAPPER_FETCH_DEFAULT:
    case FINDER_FOLDER_CONTENTS_FETCH:
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const {
          files
        } = contents;
        return mergeFiles(state, files);
      }

    case FINDER_OPEN_FOLDERS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        return contents.reduce((__state, __folderContents) => {
          const {
            files
          } = __folderContents;
          return mergeFiles(__state, files);
        }, state);
      }

    case FINDER_FILE_CREATE:
    case FINDER_FILE_CREATE_FROM_EDITOR:
    case FINDER_FILE_DUPLICATE:
    case FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE:
    case FINDER_FILE_DUPLICATE_READ_ONLY:
    case FINDER_FILE_RESTORE_DELETED:
      {
        const {
          file,
          fileType
        } = action;
        if (!file) return state;
        const fileData = getFileDataFromResult(fileType, file);
        return mergeFiles(state, [fileData]);
      }

    case FINDER_FILE_RENAME:
      {
        const {
          fileId,
          file: renamedFile,
          error
        } = action;

        if (renamedFile) {
          const {
            fileType
          } = action;
          const fileData = getFileDataFromResult(fileType, renamedFile);
          return state.set(fileId, new File(fileData));
        }

        const file = state[fileId];
        if (!file) return state;
        const fileName = error ? action.oldFileName : action.fileName;
        return state.setIn([fileId, 'name'], fileName);
      }

    case FINDER_FILE_DELETE:
      {
        const {
          fileId,
          deleted
        } = action;
        if (!deleted) return state;
        return state.without(fileId);
      }

    case FINDER_FILES_BULK_DELETE:
      {
        const {
          results
        } = action;
        if (!results) return state;
        return results.reduce((updatedState, result) => {
          return result.succeeded ? updatedState.without(result.id) : updatedState;
        }, state);
      }

    case FINDER_FILES_RECENTLY_DELETED_FETCH:
    case FINDER_FILES_RECENTLY_EDITED_FETCH:
      {
        const {
          files
        } = action;
        if (!files) return state;
        return mergeFiles(state, files);
      }

    case FINDER_ASSETS_SEARCH_FETCH:
      {
        const {
          assets
        } = action;
        if (!assets) return state;
        return mergeFiles(state, assets.filter(asset => asset.type !== FOLDER));
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        return state.without(contents.files);
      }

    case FINDER_FOLDER_PARENTS_FETCH:
      {
        const {
          files: filesWithParents
        } = action;
        if (!filesWithParents) return state;
        const files = (filesWithParents || []).map(el => el.file);
        return mergeFiles(state, files);
      }

    case FINDER_FOLDER_CONTENTS_BULK_DELETE:
      {
        const {
          files = []
        } = action;
        return files.reduce((updatedState, file) => {
          return updatedState.without(file.id);
        }, state);
      }

    case LAYOUT_RENAME:
      {
        const {
          layoutId,
          label
        } = action;
        const layout = state[layoutId];
        if (!(layout && label)) return state;
        return state.setIn([layoutId, 'name'], label);
      }

    case TEMPLATE_BUFFER_PUBLISH:
      {
        const {
          templateId,
          template
        } = action;
        const currentTemplate = state[templateId];
        if (!template || !currentTemplate) return state;
        return state.set(templateId, currentTemplate.merge({
          availableForNewContent: template.isAvailableForNewContent,
          templateType: template.templateType
        }));
      }

    case RAW_ASSET_CREATE:
      {
        const {
          rawAsset,
          fileType
        } = action;
        if (!rawAsset) return state;
        const fileData = getFileDataFromResult(fileType, rawAsset);
        return mergeFiles(state, [fileData]);
      }

    default:
      {
        return state;
      }
  }
}

function currentlyUploading(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_FILE_UPLOAD_UPDATE_PROGRESS:
      {
        return Object.assign({}, state, {
          [`${action.fullPath}/${action.fileName}`]: {
            fileName: action.fileName,
            fullPath: action.fullPath,
            folderId: action.folderId,
            progress: action.progress
          }
        });
      }

    case FINDER_FILE_UPLOAD_FINISHED:
      {
        const _ref = `${action.fullPath}/${action.fileName}`,
              newState = _objectWithoutPropertiesLoose(state, [_ref].map(_toPropertyKey));

        return newState;
      }

    default:
      {
        return state;
      }
  }
}

function removeFileResults(state, fileIds) {
  return state.filter(fileId => !~fileIds.indexOf(fileId));
}

function fileResultsReducerFactory(actionType, customHandlers = {}) {
  const {
    isFetching = state => state,
    fetchError = state => state,
    fileIds = state => state
  } = customHandlers;

  function __isFetching(state = null, action) {
    switch (action.type) {
      case actionType:
        {
          return !(action.files || action.error);
        }

      default:
        {
          return isFetching(state, action);
        }
    }
  }

  function __fetchError(state = null, action) {
    switch (action.type) {
      case actionType:
        {
          return action.error || null;
        }

      default:
        {
          return fetchError(state, action);
        }
    }
  }

  function __fileIds(state = SeamlessImmutable([]), action) {
    switch (action.type) {
      case actionType:
        {
          const {
            files = []
          } = action;
          return SeamlessImmutable(unique((files || []).map(el => el.id)));
        }

      case FINDER_FILE_DELETE:
        {
          const {
            fileId,
            deleted
          } = action;
          if (!deleted) return state;
          return removeFileResults(state, [fileId]);
        }

      case FINDER_FILES_BULK_DELETE:
        {
          const {
            results
          } = action;
          if (!results) return state;
          const deletedFileIds = results.reduce((coll, result) => {
            return result.succeeded ? coll.concat(result.id) : coll;
          }, []);
          return removeFileResults(state, deletedFileIds);
        }

      case FINDER_FOLDER_CONTENTS_BULK_DELETE:
        {
          const {
            files = []
          } = action;
          return removeFileResults(state, (files || []).map(el => el.id));
        }

      case FINDER_FOLDER_DELETE:
        {
          const {
            contents,
            deleted
          } = action;
          if (!deleted) return state;
          return removeFileResults(state, contents.files);
        }

      default:
        {
          return fileIds(state, action);
        }
    }
  }

  return combineReducers({
    isFetching: __isFetching,
    fetchError: __fetchError,
    fileIds: __fileIds
  });
}

const recentlyDeleted = fileResultsReducerFactory(FINDER_FILES_RECENTLY_DELETED_FETCH, {
  fileIds: (state, action) => {
    switch (action.type) {
      case FINDER_FILE_RESTORE_DELETED:
        {
          const {
            file
          } = action;
          if (!file) return state;
          return state.filter(__id => __id !== file.id);
        }

      default:
        {
          return state;
        }
    }
  }
});
const recentlyEdited = fileResultsReducerFactory(FINDER_FILES_RECENTLY_EDITED_FETCH);
export default combineReducers({
  byId,
  recentlyDeleted,
  recentlyEdited,
  currentlyUploading
});